import { useEffect } from "react";

type ScrollRevealOptions = {
  rootMargin?: string;
  threshold?: number;
  once?: boolean;
};

// Adds an IntersectionObserver on elements marked with data-reveal to animate them into view.
export const useScrollReveal = (options: ScrollRevealOptions = {}) => {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (typeof IntersectionObserver === "undefined") return;

    const elements = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]")
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          if (entry.isIntersecting) {
            target.classList.add("is-visible");
            if (options.once !== false) {
              observer.unobserve(target);
            }
          } else if (options.once === false) {
            target.classList.remove("is-visible");
          }
        });
      },
      {
        rootMargin: options.rootMargin ?? "0px 0px -10% 0px",
        threshold: options.threshold ?? 0.2,
      }
    );

    elements.forEach((el) => {
      const delay = el.dataset.revealDelay;
      if (delay) {
        el.style.setProperty("--reveal-delay", delay);
      }
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [options.once, options.rootMargin, options.threshold]);
};

export default useScrollReveal;
